// Article casque (déjà publié — Vol. 19 / Mai 2026)
import { ShieldCheck, AlertTriangle, CheckCircle2, Sparkles, Lightbulb } from 'lucide-react';

export const casqueArticle = {
  slug: 'bien-choisir-casque-moto',
  weekVolume: 19,
  weekYear: 2026,
  monthLabel: 'Mai 2026',
  category: 'Sécurité & équipement',
  shortTitle: 'Bien choisir son casque',
  title: 'Bien choisir son casque de moto',
  kicker: 'Article R431-1 — équipement obligatoire',
  subtitle: "Plus de 50 % des blessés conducteurs ou passagers de deux-roues motorisés sont victimes de lésions encéphaliques. Le casque, bien choisi et bien attaché, reste votre meilleur allié.",
  heroImage: '/images/articles/enquete-01-casque.png',
  heroImageAlt: "Motard en train d'attacher la mentonnière de son casque intégral homologué — geste sécurité essentiel avant tout départ",
  coverPoster: '/images/articles/poster-01-casque-horizontal.png',
  coverPosterAlt: "Poster CODE 2 ROUES — Dossier Enquête #1 : Casque, 5 étapes essentielles (bien choisir, bien attacher, bien ajuster, bien utiliser, bien entretenir)",
  posterHorizontal: '/images/articles/poster-01-casque-horizontal.png',
  posterVertical: '/images/articles/poster-01-casque.png',
  posterVerticalAlt: "Poster vertical CODE 2 ROUES — Dossier Enquête #1 : Casque, les 5 étapes essentielles (bien choisir, attacher, ajuster, utiliser, entretenir)",
  topRightKicker: { line1: 'Conseil ECSR', line2: 'Sécurité & équipement' },
  readTimeMin: 4,
  seoDescription: "Casque jet ou intégral ? Norme ECE 22-06, sanctions, conseils d'essai et d'entretien. Le guide officiel ECSR pour bien choisir son casque moto en 2026.",
  seoKeywords: 'choisir casque moto, ECE 22-06, casque intégral, casque jet, R431-1, équipement moto obligatoire',
  about: "Sécurité routière du motard, équipement obligatoire, casque homologué",
  source: { label: 'Sécurité routière', url: 'https://www.securite-routiere.gouv.fr', refs: 'Article R431-1 du Code de la route, norme ECE 22-06.' },
  chapo: {
    kicker: 'Pourquoi cet article',
    body: "Le port du casque correctement attaché est <strong>obligatoire</strong> pour tout conducteur ou passager d'un deux-roues motorisé (article <strong>R431-1 du Code de la route</strong>). Mais au-delà de l'obligation légale, c'est <em>l'élément qui sauve le plus de vies</em> en cas de chute.",
    sideStat: {
      kicker: 'Chiffre choc',
      big: '+50 %',
      body: "des blessés en deux-roues motorisé subissent des <strong>lésions encéphaliques</strong>. Cause principale de handicap lourd et de décès.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "Le port du casque homologué et correctement attaché est obligatoire pour tout conducteur ou passager d'un véhicule à moteur à deux ou trois roues.",
    source: 'Article R431-1 du Code de la route',
  },
  sections: [
    {
      part: 'Partie 1 — Le risque',
      title: 'Rouler sans casque : quels sont les risques ?',
      paragraphs: [
        "L'absence de casque est un <strong>facteur aggravant majeur</strong>. En 2017, 9 conducteurs et 1 passager tués ne portaient pas de casque (9 % de la mortalité des cyclomotoristes).",
      ],
      cards: [
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Sanction', title: 'Non-port du casque', body: "Amende forfaitaire de <strong>4ᵉ classe</strong> (135 €) + retrait de <strong>3 points</strong>. S'applique au conducteur ET au passager." },
        { icon: ShieldCheck, accent: '#16a34a', kicker: 'Obligation', title: 'Norme ECE 22-06', body: "Depuis 2024, seuls les casques homologués <strong>ECE 22-06</strong> peuvent être commercialisés." },
      ],
      cardsCols: 2,
    },
    {
      part: 'Partie 2 — Le choix',
      title: 'Plutôt « jet » ou intégral ?',
      compareCards: [
        {
          accent: '#16a34a', kicker: 'Recommandé', title: 'Le casque intégral',
          intro: "La meilleure protection — enveloppe toute la tête.",
          points: [
            { ok: true, text: 'Enveloppe entièrement la tête (haut, côtés, menton)' },
            { ok: true, text: 'Adapté aux longs trajets et autoroute' },
            { ok: true, text: "Efficace en cas d'intempéries (pluie, vent, froid)" },
            { ok: true, text: "Réduit l'impact du bruit et du vent sur le visage" },
            { ok: true, text: 'Dévie insectes et objets projetés' },
          ],
        },
        {
          accent: '#cf2031', kicker: 'Protection minimale', title: 'Le casque « jet »',
          intro: 'Légalement valable mais largement insuffisant.',
          points: [
            { ok: false, text: 'Ne protège <strong>ni le visage ni le menton</strong>' },
            { ok: false, text: 'Insectes, gravillons, vent direct dans les yeux' },
            { ok: false, text: 'Bruit et fatigue accrus sur trajets longs' },
            { ok: true, text: 'Convient à de très courts trajets urbains à faible vitesse' },
          ],
          footer: "« On s'habille pour la chute, pas pour la balade. »",
        },
      ],
    },
    {
      part: 'Partie 3 — Décrypter les normes',
      title: 'Repérer les normes certifiées',
      intro: "Votre casque doit répondre à la <strong>norme européenne ECE 22-06</strong>. Une <strong>étiquette blanche</strong> attestant du type d'homologation est cousue sur la jugulaire.",
      normCards: [
        { code: 'PJ', color: '#1d4ed8', title: 'Protective-Jet', body: "Casque <strong>modulable</strong> homologué jet ET intégral. Utilisable ouvert ou fermé." },
        { code: 'J', color: '#cf2031', title: 'Jet', body: "Casque <strong>jet</strong> simple. Protection minimale." },
        { code: 'P', color: '#16a34a', title: 'Protective', body: "Casque <strong>intégral</strong>. Protection maximale." },
      ],
      callouts: [
        { icon: Lightbulb, accent: '#1d4ed8', body: "<strong>La visière</strong> doit être transparente et homologuée (lettre <strong>E</strong> + chiffre du pays). <br/><strong>Visières fumées ou miroir : interdites en circulation.</strong>" },
      ],
    },
    {
      part: 'Partie 4 — À éviter',
      title: "Le danger des casques d'occasion",
      paragraphs: [
        "La <strong>Sécurité routière déconseille fortement</strong> d'acheter un casque déjà utilisé. Durée de vie moyenne : <strong>5 ans</strong>.",
      ],
      cards: [
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Risque 1', title: 'Précédents chocs invisibles', body: "Si le casque a déjà subi un choc, cela peut <strong>ne pas se voir à l'œil nu</strong>. Le casque pourrait <em>exploser</em> au prochain choc." },
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Risque 2', title: 'Hygiène', body: 'La transpiration véhicule bactéries et mauvaises odeurs. Un casque ne doit être utilisé que par <strong>une seule personne</strong>.' },
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Risque 3', title: 'Usure invisible', body: 'Au-delà de 5 ans, certains casques se dégradent : rayures, fissures, attache cassée, mousse défoncée.' },
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'Risque 4', title: 'Norme obsolète', body: "Un casque ECE 22-04 ou 22-05 ancien <strong>n'offre plus la protection</strong> du standard ECE 22-06." },
      ],
      cardsCols: 2,
    },
  ],
  quiz: [
    { id: 'q1', question: 'Quelle est la norme européenne actuelle obligatoire pour un casque de moto ?', options: ['ECE 22-04', 'ECE 22-05', 'ECE 22-06', 'NF Z 04-100'], correctIndex: 2, explain: "Depuis 2024, seuls les casques homologués ECE 22-06 peuvent être commercialisés. La norme 22-05 n'est plus délivrée." },
    { id: 'q2', question: 'Quelle est la sanction en cas de non-port du casque (conducteur ou passager) ?', options: ['Avertissement uniquement', 'Amende 4ᵉ classe + retrait de 3 points', 'Amende 5ᵉ classe + retrait de 6 points', 'Suspension immédiate du permis'], correctIndex: 1, explain: "Article R431-1 : amende forfaitaire de 4ᵉ classe (135 €) + retrait de 3 points sur le permis." },
    { id: 'q3', question: 'Combien de stickers rétro-réfléchissants doit comporter un casque conforme ?', options: ['2 (avant + arrière)', '3 (avant + 2 côtés)', '4 (avant, arrière, 2 côtés)', "Aucun, c'est facultatif"], correctIndex: 2, explain: "Un casque conforme doit comporter 4 stickers rétro-réfléchissants symétriques : un devant, un derrière, un de chaque côté." },
    { id: 'q4', question: "Quelle est la durée de vie moyenne recommandée d'un casque ?", options: ['2 ans', '5 ans', '10 ans', "Illimitée tant qu'il n'a pas chuté"], correctIndex: 1, explain: "5 ans. Au-delà, la mousse interne EPS se dégrade. Et après tout choc violent, même invisible, le casque doit être remplacé." },
    { id: 'q5', question: 'Quel type de visière est INTERDIT en circulation ?', options: ['Transparente homologuée E', 'Photochromique homologuée', 'Fumée ou miroir', 'Avec film anti-buée Pinlock'], correctIndex: 2, explain: "Les visières fumées ou miroir sont interdites en circulation : elles dégradent la perception nocturne et empêchent le contact visuel." },
  ],
  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: 'Découvre les autres équipements obligatoires',
    body: "Casque, gants homologués, gilet rétro-réfléchissant : connais l'ensemble des EPI obligatoires sur deux-roues motorisé.",
    primary: { label: "Voir l'équipement complet", to: '/magazine/equipement-complet-motard-2026' },
    secondary: { label: 'Sanctions & barèmes', to: '/sanctions-baremes' },
  },
};
